import React, { FC, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { ReactComponent as ArrowLeft } from 'images/icons/arrow-left.svg';
import { ReactComponent as Copy } from 'images/icons/copy.svg';

import MediaPlayer from 'components/MediaPlayer';

import { useAppDispatch, useAppSelector } from '../../hooks';
import { Interception } from '../../interfaces/interceptions';
import { getInterceptionTranscriptsByIdThunk } from '../../store/slices/interception/actions';
import { interceptionTranscriptsSelector } from '../../store/slices/interception/selectors';
import { copyToClipboard } from '../../utils';

export interface InterceptionsInfoSidebarProps {
  interception: Interception;
  closeSidebar: () => void;
}

const InterceptionsInfoSidebar: FC<InterceptionsInfoSidebarProps> = ({
  interception,
  closeSidebar,
}) => {
  const [selectedSoundTab, setSelectedSoundTab] = useState(true);
  const transcripts = useAppSelector(interceptionTranscriptsSelector);
  const {
    id,
    transmission,
    server_client,
    frequency,
    duration,
    size_kb,
    protocol,
    note,
    from_whom,
    to_whom,
    summary,
    wavs,
  } = interception;

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(getInterceptionTranscriptsByIdThunk(id));
  }, [id]);

  const ruTextForCopy = useMemo(() => {
    return transcripts.reduce(
      (result, value) => result + `${value.ru_transcript}\n`,
      ''
    );
  }, [transcripts]);

  const wavUrl = useMemo(() => {
    return wavs.find((wav) => wav.length);
  }, wavs);

  const originalTextForCopy = useMemo(() => {
    return transcripts.reduce(
      (result, value) => result + `${value.original_transcript}\n`,
      ''
    );
  }, [transcripts]);

  return (
    <div
      className={`absolute w-[520px] right-0 !h-[calc(100vh-100px)] bg-dark rounded-[10px] overflow-auto mr-1.5`}
    >
      <div className="header__links">
        <div className="flex flex-row w-full">
          <div
            className={cn(
              'flex gap-1 items-center w-full justify-center',
              {
                'header-link__active': selectedSoundTab,
                'bg-light': !selectedSoundTab,
              },
              'py-2 px-6'
            )}
            onClick={() => setSelectedSoundTab(true)}
          >
            <p className="header-link__title tpg-b1">Звук</p>
          </div>
          <div
            className={cn(
              'flex gap-1 items-center w-full justify-center',
              {
                'header-link__active': !selectedSoundTab,
                'bg-light': selectedSoundTab,
              },
              'py-2 px-6'
            )}
            onClick={() => setSelectedSoundTab(false)}
          >
            <p className="header-link__title tpg-b1">Параметры</p>
          </div>
        </div>
      </div>
      {selectedSoundTab ? (
        <div className="p-6 h-full">
          <div className="flex flex-col gap-3">
            {wavUrl ? (
              <MediaPlayer width="100%" height="40px" url={wavUrl} />
            ) : null}
            <div className="flex justify-between items-center">
              <div className="text-tpg_title font-bold">Краткая сводка</div>
              <div
                className="text-[11px] tpg-c2 cursor-pointer flex items-cetner gap-1"
                onClick={() => copyToClipboard(summary)}
              >
                <Copy className="fill-current text-white" />
                <div>Скопировать</div>
              </div>
            </div>
            <div className="p-3 rounded-[10px] bg-light">{summary}</div>
          </div>
          <div className="flex justify-between items-center mt-3">
            <div className="text-tpg_title font-bold">Перевод</div>
            <div
              className="text-[11px] tpg-c2 cursor-pointer flex items-cetner gap-1"
              onClick={() => copyToClipboard(ruTextForCopy)}
            >
              <Copy className="fill-current text-white" />
              <div>Скопировать</div>
            </div>
          </div>
          {transcripts?.map((segment) => (
            <div
              key={segment.end_sec}
              className="flex flex-col gap-[8px] p-3 rounded-[10px] bg-light mt-3"
            >
              <div className="font-bold">
                Абонент 1 (
                <span className="text-bright_product">
                  {segment.speaker_id}
                </span>
                ):
              </div>
              <div>{segment.ru_transcript}</div>
            </div>
          ))}
          <div className="flex justify-between items-center mt-3">
            <div className="text-tpg_title font-bold">Оригинал</div>
            <div
              className="text-[11px] tpg-c2 cursor-pointer flex items-cetner gap-1"
              onClick={() => copyToClipboard(originalTextForCopy)}
            >
              <Copy className="fill-current text-white" />
              <div>Скопировать</div>
            </div>
          </div>
          {transcripts?.map((segment) => (
            <div
              key={segment.end_sec}
              className="flex flex-col gap-[8px] p-3 rounded-[10px] bg-light mt-3"
            >
              <div className="font-bold">
                Абонент 1 (
                <span className="text-bright_product">
                  {segment.speaker_id}
                </span>
                ):
              </div>
              <div>{segment.original_transcript}</div>
            </div>
          ))}
        </div>
      ) : (
        <div className="p-6 h-full">
          <div>Основная информация</div>
          <div className="mt-2 border border-1 border-solid border-bright_product rounded-[10px] flex flex-col">
            <div className="flex flex-col px-4 py-2 border-b border-solid border-b-bright_product">
              <div className="text-[11px] tpg-c2 text-tpg_light">Передача</div>
              <div className="text-tpg_base">{transmission}</div>
            </div>
            <div className="flex flex-col px-4 py-2 border-b border-solid border-b-bright_product">
              <div className="text-[11px] tpg-c2 text-tpg_light">
                Подразделение
              </div>
              <div className="text-tpg_base">3 ОПСН 71 ОЕБр</div>
            </div>
            <div className="flex flex-col px-4 py-2 border-b border-solid border-b-bright_product">
              <div className="text-[11px] tpg-c2 text-tpg_light">
                Сервер/Клиент
              </div>
              <div className="text-tpg_base">{server_client}</div>
            </div>
            <div className="flex flex-col px-4 py-2 border-b border-solid border-b-bright_product">
              <div className="text-[11px] tpg-c2 text-tpg_light">От кого</div>
              <div className="text-tpg_base">{from_whom}</div>
            </div>
            <div className="flex flex-col px-4 py-2">
              <div className="text-[11px] tpg-c2 text-tpg_light">Кому</div>
              <div className="text-tpg_base">{to_whom}</div>
            </div>
          </div>
          <div className="mt-4">Техническая информация</div>
          <div className="mt-2 border border-1 border-solid border-bright_product rounded-[10px] flex flex-col">
            <div className="flex flex-col px-4 py-2 border-b border-1 border-solid border-b-bright_product">
              <div className="text-[11px] tpg-c2 text-tpg_light">
                Частота, МГЦ
              </div>
              <div className="text-tpg_base">{frequency}</div>
            </div>
            <div className="flex flex-col px-4 py-2 border-b border-solid border-b-bright_product">
              <div className="text-[11px] tpg-c2 text-tpg_light">
                Длительность
              </div>
              <div className="text-tpg_base">{duration}</div>
            </div>
            <div className="flex flex-col px-4 py-2 border-b border-solid border-b-bright_product">
              <div className="text-[11px] tpg-c2 text-tpg_light">Размер</div>
              <div className="text-tpg_base">{size_kb} KB</div>
            </div>
            <div className="flex flex-col px-4 py-2 border-b border-solid border-b-bright_product">
              <div className="text-[11px] tpg-c2 text-tpg_light">Протокол</div>
              <div className="text-tpg_base">{protocol}</div>
            </div>
            <div className="flex flex-col px-4 py-2">
              <div className="text-[11px] tpg-c2 text-tpg_light">
                Особые отметки
              </div>
              <div className="text-tpg_base">{note}</div>
            </div>
          </div>
        </div>
      )}
      <div
        className="fixed w-full bottom-0 z-[1] bg-light py-[6px] px-4 tpg-c2 text-tpg_base cursor-pointer flex items-center"
        onClick={closeSidebar}
      >
        <ArrowLeft className="fill-current text-white" />
        <div className="relavite top-[1px]">Назад</div>
      </div>
    </div>
  );
};

export default InterceptionsInfoSidebar;
